import React, { useMemo, useState } from 'react';
import { Project, ProjectType } from '../../../types/Project';

import { StandoutDropdown } from '../../../components/StandoutDropdown';
import FiltersCard from '../../../components/FiltersCard';
import { useDynamicFilterOptions } from '../../../hooks/Filters/useDynamicFilterOptions';
import { FilterGrid, FilterField, Form } from '@/components/ui';

// Define types for project types
type ProjectTypeOption = 'Routine' | 'Quantified' | 'H200' | 'CMC/MVI' | 'Internal' | 'Other Revenue';
const PROJECT_TYPES: ProjectTypeOption[] = ['Routine', 'Quantified', 'H200', 'CMC/MVI', 'Internal', 'Other Revenue'];

/**
 * Project lifecycle order for the Status filter.
 *
 * Projects do NOT use task stages — they use their own lowercase states
 * (see `ProjectStatus` in types/Project.ts), so `TASK_STATUS_SEQUENCE` does not apply here.
 * Order follows the way a project actually moves: proposed → planned → active → sent back for
 * revisions → completed. `cancelled` sits outside the progression (a project can be cancelled
 * from anywhere), so it sorts last — the same convention `TASK_STATUS_SEQUENCE` documents.
 */
const PROJECT_STATUS_SEQUENCE: string[] = [
  'pending',
  'planned',
  'active',
  'need_revisions',
  'completed',
  'cancelled',
];

/** Display labels for project statuses. `pending` is shown as "Proposed", matching ProjectModal. */
const PROJECT_STATUS_LABELS: Record<string, string> = {
  pending: 'Proposed',
  planned: 'Planned',
  active: 'Active',
  need_revisions: 'Needs Revisions',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

// Exported for ProjectDetailsPage's status badge — projects use their own lowercase lifecycle
// (see PROJECT_STATUS_SEQUENCE above), so this is the one place that formats it; a second copy
// there would be exactly the kind of drifting local status map StatusBadge exists to avoid.
export const formatProjectStatus = (status: string): string =>
  PROJECT_STATUS_LABELS[status?.toLowerCase()] ??
  (status ? status.charAt(0).toUpperCase() + status.slice(1) : status);

/** Orders project statuses by PROJECT_STATUS_SEQUENCE; unknown values keep their order and sort last. */
const sortProjectStatuses = (statuses: string[]): string[] => {
  const rank = (s: string) => {
    const i = PROJECT_STATUS_SEQUENCE.indexOf(s?.toLowerCase() || '');
    return i === -1 ? PROJECT_STATUS_SEQUENCE.length : i;
  };
  return [...statuses]
    .map((status, i) => ({ status, i, r: rank(status) }))
    .sort((a, b) => a.r - b.r || a.i - b.i)
    .map((x) => x.status);
};

/**
 * Filter values travel as comma-separated strings (URL param -> query string -> Prisma `in`),
 * so the multiselect dropdowns read/write through these two helpers.
 */
const parseSelected = (value?: string): string[] =>
  value ? value.split(',').map((v) => v.trim()).filter(Boolean) : [];

const joinSelected = (values: (string | number)[]): string =>
  values.map((v) => String(v).trim()).filter(Boolean).join(',');

interface ProjectFiltersProps {
  serviceAreas: string[];
  filters: {
    serviceArea?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    type?: string;
    search?: string;
  };
  projects: Project[];
  filterOptions?: {
    types: string[];
    statuses: string[];
    serviceAreas: { id: number; name: string }[];
  };
  onFilterChange: (name: string, value: string) => void;
  onClearFilters?: () => void;
  showApplyButton?: boolean;
  onApply?: () => void;
  applyDisabled?: boolean;
  hasActiveFilters?: boolean;
}

const ProjectFilters: React.FC<ProjectFiltersProps> = ({
  serviceAreas,
  filters,
  projects,
  filterOptions,
  onFilterChange,
  onClearFilters,
  showApplyButton = false,
  onApply,
  applyDisabled = false,
  hasActiveFilters = false,
}) => {
  // Selected values, decoded from the comma-separated filter strings.
  const selectedTypes = useMemo(() => parseSelected(filters.type), [filters.type]);
  const selectedServiceAreas = useMemo(() => parseSelected(filters.serviceArea), [filters.serviceArea]);
  const selectedStatuses = useMemo(() => parseSelected(filters.status), [filters.status]);

  // Filter projects based on all filters except type and serviceArea (for dynamic dropdowns)
  const filteredProjects = useMemo(() => {
    const startDate = filters.startDate ? new Date(filters.startDate) : null;
    const endDate = filters.endDate ? new Date(filters.endDate) : null;
    const statusSet = new Set(selectedStatuses);
    return projects.filter(project => {
      const projectStartDate = project.startDate ? new Date(project.startDate) : null;
      return (
        (statusSet.size === 0 || statusSet.has(project.status)) &&
        (!startDate || !projectStartDate || projectStartDate >= startDate) &&
        (!endDate || !projectStartDate || projectStartDate <= endDate)
      );
    });
  }, [projects, selectedStatuses, filters.startDate, filters.endDate]);

  // Always call hooks first
  const dynamicOptions = useDynamicFilterOptions(filteredProjects, ['type', 'serviceArea']);

  // Dynamic options for dropdowns - use filterOptions if available, otherwise derive from current page
  const typeOptions = filterOptions?.types || (dynamicOptions.type as ProjectTypeOption[] || []);
  const serviceAreaOptionsFromFilter = filterOptions?.serviceAreas || (dynamicOptions.serviceArea as { id: string | number, name: string }[] || []);
  // Statuses are presented in project lifecycle order, not the arbitrary order the API returns them in.
  const statusOptions = useMemo(
    () => sortProjectStatuses(filterOptions?.statuses || ['active', 'completed']),
    [filterOptions?.statuses]
  );

  // Deduplicate service areas by id or name
  const dedupedServiceAreas = Array.from(
    new Map(
      serviceAreaOptionsFromFilter
        .filter((area): area is { id: string | number, name: string } => !!area && !!area.id && !!area.name)
        .map(area => [area.id, area])
    ).values()
  );

  // Get the formatted service area name
  const getServiceAreaName = (id: string) => {
    const found = dedupedServiceAreas.find((sa) => sa.id.toString() === id);
    return found ? found.name : id;
  };

  // Check if any filter is applied (local function for dynamic options)
  const checkActiveFilters = () => {
    return Object.values(filters).some(value => value !== '');
  };

  // Handle clear all filters
  const handleClearFilters = () => {
    if (onClearFilters) {
      onClearFilters();
    }
  };

  return (
    <>
      {/* Filters Card */}
       <FiltersCard 
         title="Project Filters"
         subtitle="Filter projects by type, service area, status, and date range"
         collapsible={true}
         defaultCollapsed={false}
         showClearButton={true}
         onClear={handleClearFilters}
         hasActiveFilters={hasActiveFilters}
         showApplyButton={showApplyButton}
         onApply={onApply}
         applyDisabled={applyDisabled}
       >
        <FilterGrid>
          <FilterField>
            <StandoutDropdown<ProjectTypeOption>
              type="multiselect"
              options={typeOptions.filter((type) => PROJECT_TYPES.includes(type as ProjectTypeOption)) as ProjectTypeOption[]}
              value={selectedTypes}
              onChange={(value) => onFilterChange('type', joinSelected(value))}
              getValue={(type) => type}
              formatOption={(type) => type.toUpperCase()}
              placeholder="All Types"
              className="tw-w-full"
              label="Project Type"
              noOptionsMessage="No project types available for selected filters"
            />
          </FilterField>
          <FilterField>
            <StandoutDropdown<string>
              type="multiselect"
              options={dedupedServiceAreas.map((area) => area.id.toString())}
              value={selectedServiceAreas}
              onChange={(value) => onFilterChange('serviceArea', joinSelected(value))}
              getValue={(area) => area}
              formatOption={(area) => {
                const found = dedupedServiceAreas.find((sa) => sa.id.toString() === area);
                return found ? found.name : area;
              }}
              placeholder="All Service Areas"
              className="tw-w-full"
              label="Service Area"
              noOptionsMessage="No service areas available for selected filters"
            />
          </FilterField>
          <FilterField>
            <StandoutDropdown<string>
              type="multiselect"
              options={statusOptions}
              value={selectedStatuses}
              onChange={(value) => onFilterChange('status', joinSelected(value))}
              getValue={(status) => status}
              formatOption={(status) => formatProjectStatus(status)}
              placeholder="All Statuses"
              className="tw-w-full"
              label="Status"
              noOptionsMessage="No statuses available for selected filters"
              // statusOptions is already ordered by PROJECT_STATUS_SEQUENCE, so opt out of the
              // dropdown's default alphabetical sort and let the lifecycle order reach the render.
              preserveOptionsOrder
            />
          </FilterField>
          <FilterField>
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              size="sm"
              name="startDate"
              value={filters.startDate || ''}
              onChange={(e) => onFilterChange(e.target.name, e.target.value)}
            />
          </FilterField>
          <FilterField>
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              size="sm"
              name="endDate"
              value={filters.endDate || ''}
              onChange={(e) => onFilterChange(e.target.name, e.target.value)}
            />
          </FilterField>
        </FilterGrid>
      </FiltersCard>
    </>
  );
};

export default ProjectFilters; 